import { useEffect, useMemo, useState } from "react";
import {
  Alert, Autocomplete, Backdrop, Box, Button, Chip, CircularProgress, Divider,
  Paper, Snackbar, Stack, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tabs, TextField, Typography, useTheme,
} from "@mui/material";
import {
  AccountBalanceWalletOutlined, ArrowBackOutlined, CalendarMonthOutlined,
  PaymentsOutlined, PersonOutline, ReceiptLongOutlined, SaveOutlined,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConnString from "../../../ConnString.js";

const apiRoot = ConnString.connstring;
const today = () => {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};
const dateOnly = (value) => String(value || "").slice(0, 10);
const branchCode = (branch) => String(branch?.Branch ?? branch?.BranchCode ?? branch?.id ?? "");
const branchName = (branch) => String(branch?.LocName ?? branch?.BranchName ?? branchCode(branch));
const borrowerCode = (borrower) => String(borrower?.AgentCode ?? borrower?.AGENTCODE ?? "");
const borrowerName = (borrower) => String(borrower?.FullName ?? borrower?.Name ?? borrowerCode(borrower));
const numberValue = (value) => Number(String(value ?? "").replace(/,/g, "")) || 0;
const money = (value) => numberValue(value).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const rateText = (value) => Number(value || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 6, useGrouping: false });
const editableMoney = (value) => {
  const cleaned = String(value ?? "").replace(/,/g, "").replace(/[^0-9.]/g, "");
  const [whole = "", ...decimalParts] = cleaned.split(".");
  const decimal = decimalParts.join("").slice(0, 2);
  const wholeNumber = whole ? Number(whole).toLocaleString("en-PH") : "";
  return decimalParts.length ? `${wholeNumber}.${decimal}` : wholeNumber;
};
const editableRate = (value) => {
  const cleaned = String(value ?? "").replace(/,/g, "").replace(/[^0-9.]/g, "");
  const [whole = "", ...decimalParts] = cleaned.split(".");
  const decimal = decimalParts.join("").slice(0, 6);
  return decimalParts.length ? `${whole}.${decimal}` : whole;
};
const paymentsPerYear = {
  Weekly: 52, "Semi-Monthly": 24, Monthly: 12, "Bi-Monthly": 6,
  Quarterly: 4, "Semi-Annual": 2, Annual: 1,
};
const addLoanTerm = (startDate, termValue, unit) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !Number.isInteger(termValue) || termValue <= 0) return "";
  const [year, month, day] = startDate.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (unit === "Days") date.setUTCDate(date.getUTCDate() + termValue);
  else if (unit === "Weeks") date.setUTCDate(date.getUTCDate() + (termValue * 7));
  else if (unit === "Months" || unit === "Years") {
    const monthsToAdd = unit === "Years" ? termValue * 12 : termValue;
    const targetMonth = month - 1 + monthsToAdd;
    const lastDay = new Date(Date.UTC(year, targetMonth + 1, 0)).getUTCDate();
    date.setUTCFullYear(year, targetMonth, Math.min(day, lastDay));
  } else return "";
  return date.toISOString().slice(0, 10);
};
const calculateLoanSummary = ({ EffectivityDate, LoanTerms, LoanTermsCons, TimeBased, LoanAmount, InterestRate, InterestCons, InterestPaymentTerms }) => {
  const maturityDate = addLoanTerm(EffectivityDate, Number(LoanTerms), LoanTermsCons);
  if (!maturityDate) return { MaturityDate: "", TotalInterest: "0.00", MaturityValue: "0.00", MonthlyPayment: "0.00" };
  const start = new Date(`${EffectivityDate}T00:00:00.000Z`);
  const maturity = new Date(`${maturityDate}T00:00:00.000Z`);
  const loanDays = Math.max(0, Math.round((maturity.getTime() - start.getTime()) / 86400000));
  const basisDays = TimeBased === "Term(365days)" ? 365 : 360;
  const principal = numberValue(LoanAmount);
  const rate = numberValue(InterestRate);
  const annualRate = rate / 100;
  const numberOfMonths = Math.max(1, Math.round((loanDays / basisDays) * 12));
  let calculatedInterest;
  if (InterestCons === "Monthly Add-On") {
    calculatedInterest = principal * (rate / 100) * numberOfMonths;
  } else if (InterestCons === "Effective Interest") {
    const frequency = paymentsPerYear[InterestPaymentTerms] || 12;
    const numberOfPayments = Math.max(1, Math.round((loanDays / basisDays) * frequency));
    const periodicRate = annualRate / frequency;
    const periodicPayment = periodicRate === 0 ? principal / numberOfPayments : principal * (periodicRate / (1 - Math.pow(1 + periodicRate, -numberOfPayments)));
    calculatedInterest = (periodicPayment * numberOfPayments) - principal;
  } else if (InterestCons === "Compounded") {
    calculatedInterest = principal * (Math.pow(1 + (annualRate / basisDays), loanDays) - 1);
  } else {
    calculatedInterest = principal * annualRate * (loanDays / basisDays);
  }
  const totalInterest = Math.round(calculatedInterest * 100) / 100;
  return {
    MaturityDate: maturityDate,
    TotalInterest: money(totalInterest),
    MaturityValue: money(principal + totalInterest),
    MonthlyPayment: money((principal + totalInterest) / numberOfMonths),
  };
};

const principalSchedule = (form, summary) => {
  const principalCents = Math.round(numberValue(form.LoanAmount) * 100);
  const start = new Date(`${form.EffectivityDate}T00:00:00.000Z`);
  const maturity = new Date(`${summary.MaturityDate}T00:00:00.000Z`);
  if (!principalCents || Number.isNaN(start.getTime()) || Number.isNaN(maturity.getTime())) return [];
  const days = Math.round((maturity.getTime() - start.getTime()) / 86400000);
  if (days <= 0) return [];
  const basis = form.TimeBased === "Term(365days)" ? 365 : 360;
  const count = Math.max(1, Math.round((days / basis) * (paymentsPerYear[form.InterestPaymentTerms] || 12)));
  const periodicRate = numberValue(form.InterestRate) / 100 / (paymentsPerYear[form.InterestPaymentTerms] || 12);
  const regularPayment = periodicRate ? (principalCents / 100) * periodicRate / (1 - Math.pow(1 + periodicRate, -count)) : (principalCents / 100) / count;
  const rows = [];
  let priorCents = 0;
  for (let paymentNo = 1; paymentNo <= count; paymentNo += 1) {
    const exponent = paymentNo - 1;
    const rawBalance = form.InterestCons === "Effective Interest"
      ? periodicRate
        ? (principalCents / 100) * Math.pow(1 + periodicRate, exponent) - regularPayment * ((Math.pow(1 + periodicRate, exponent) - 1) / periodicRate)
        : (principalCents / 100) - regularPayment * exponent
      : (principalCents / 100) * (1 - exponent / count);
    const interest = Math.round(rawBalance * periodicRate * 100) / 100;
    const scheduledCents = form.InterestCons === "Effective Interest"
      ? Math.round((regularPayment - interest) * 100)
      : Math.round(principalCents / count);
    const amountCents = paymentNo === count ? principalCents - priorCents : scheduledCents;
    const dueDate = new Date(start);
    dueDate.setUTCDate(dueDate.getUTCDate() + (paymentNo === count ? days : Math.round(days * paymentNo / count)));
    rows.push({ PaymentNo: paymentNo, DueDate: dueDate.toISOString().slice(0, 10), PrincipalPayment: amountCents / 100 });
    priorCents += amountCents;
  }
  return rows;
};
const interestPaymentTermOptions = [
  "Weekly", "Semi-Monthly", "Monthly", "Bi-Monthly", "Quarterly", "Semi-Annual", "Annual",
];
const principalPaymentTermOptions = ["Align with Interest payment", "At Maturity Date"];

const initialForm = {
  DocNo: "", Branch: "", DocDate: today(), Type: "NEW", Borrower: "", RefNo: "",
  TimeBased: "Regular(360days)", Particulars: "", EffectivityDate: today(), LoanAmount: "0.00",
  InterestRate: "0.00", InterestCons: "Monthly Add-On", LoanTerms: "", LoanTermsCons: "Months",
  MaturityDate: "", InterestPaymentTerms: "Monthly", PrincipalPaymentTerms: "Align with Interest payment",
  TotalInterest: "0.00", MaturityValue: "0.00", Status: "New",
};

const SectionHeader = ({ icon, title, subtitle, motif }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
    <Box sx={{ width: 34, height: 34, flexShrink: 0, display: "grid", placeItems: "center", borderRadius: 2, bgcolor: motif.panel, color: motif.main }}>
      {icon}
    </Box>
    <Box>
      <Typography variant="subtitle1" fontWeight={800} color={motif.text} lineHeight={1.2}>{title}</Typography>
      <Typography variant="caption" color={motif.muted}>{subtitle}</Typography>
    </Box>
  </Stack>
);

const AmountField = ({ label, field, value, onChange, onBlur, disabled, required, sx }) => (
  <TextField
    size="small"
    label={`${label}${required ? " *" : ""}`}
    value={value}
    onChange={(event) => onChange(field, editableMoney(event.target.value))}
    onBlur={() => onBlur(field)}
    disabled={disabled}
    inputProps={{ inputMode: "decimal" }}
    InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: "text.secondary" }}>₱</Typography> }}
    sx={sx}
  />
);

const Loans = () => {
  const theme = useTheme();
  const motif = theme.palette.motif;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestedMode = String(params.get("mode") || "new").toLowerCase();
  const recID = params.get("recid") || "";
  const routeBranch = params.get("branch") || "";
  const editing = requestedMode === "edit";
  const viewing = requestedMode === "view";
  const cloning = requestedMode === "clone";
  const compID = sessionStorage.getItem("compID") || "";
  const userID = sessionStorage.getItem("userID") || "";
  const [form, setForm] = useState(initialForm);
  const [branches, setBranches] = useState([]);
  const [borrowers, setBorrowers] = useState([]);
  const [referenceLoans, setReferenceLoans] = useState([]);
  const [journalAccounts, setJournalAccounts] = useState([]);
  const [savedJournalRows, setSavedJournalRows] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const selectedBorrower = useMemo(
    () => borrowers.find((borrower) => borrowerCode(borrower) === String(form.Borrower)) || null,
    [borrowers, form.Borrower],
  );
  const availableBorrowers = useMemo(
    () => borrowers.filter((borrower) => !form.Branch || String(borrower.Branch || "") === String(form.Branch)),
    [borrowers, form.Branch],
  );
  const referenceRequired = form.Type === "RENEW" || form.Type === "REVISE";
  const availableReferenceLoans = useMemo(
    () => referenceLoans.filter((loan) => (
      String(loan.Branch || "") === String(form.Branch)
      && String(loan.Borrower || "") === String(form.Borrower)
      && String(loan.RecID || "") !== String(recID || "")
    )),
    [form.Borrower, form.Branch, recID, referenceLoans],
  );
  const selectedReferenceLoan = useMemo(
    () => referenceLoans.find((loan) => String(loan.DocNo || "") === String(form.RefNo || ""))
      || (form.RefNo ? { DocNo: form.RefNo } : null),
    [form.RefNo, referenceLoans],
  );
  const computedSummary = useMemo(
    () => calculateLoanSummary(form),
    [form],
  );
  const scheduledPrincipal = useMemo(() => principalSchedule(form, computedSummary), [form, computedSummary]);
  const journalRows = useMemo(() => {
    if (viewing && savedJournalRows.length) return savedJournalRows;
    const account = (code) => journalAccounts.find((item) => String(item.Code).toUpperCase() === code);
    const amount = numberValue(form.LoanAmount);
    return [
      ...scheduledPrincipal.map((payment) => ({
        ...account("GLLENAR"), Code: "GLLENAR", AgentCode: form.Borrower,
        Debit: payment.PrincipalPayment, Credit: 0,
        Particulars: `Principal payment ${payment.PaymentNo} due ${payment.DueDate} - ${form.Particulars}`.slice(0, 500),
      })),
      { ...account("GLLENAP"), Code: "GLLENAP", AgentCode: form.Borrower, Debit: 0, Credit: amount },
    ];
  }, [form.Borrower, form.LoanAmount, form.Particulars, journalAccounts, savedJournalRows, scheduledPrincipal, viewing]);
  const journalConfigured = journalRows.length > 1 && journalRows.every((row) => row.AccountCode && row.AccountTitle);

  const post = async (path, body) => {
    const response = await fetch(`${apiRoot}${path}`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const raw = await response.text();
    let data;
    try { data = JSON.parse(raw); } catch { throw new Error("The server returned an invalid response."); }
    if (!response.ok || data.success === false) throw new Error(data.message || "Request failed.");
    return data;
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const options = await post("/api/lending/loans/options", { compID, userID });
        if (!active) return;
        setBranches(options.branches || []);
        setBorrowers(options.borrowers || []);
        setReferenceLoans(options.referenceLoans || []);
        setJournalAccounts(options.journalAccounts || []);
        const defaultBranch = (options.branches || []).find((item) => branchCode(item) === routeBranch)
          || (options.branches || []).find((item) => Boolean(item.DefaultBranch))
          || options.branches?.[0];

        if (editing || viewing || cloning) {
          if (!recID) throw new Error("No loan transaction was selected.");
          const loaded = await post("/api/lending/loans/load", { compID, userID, recID, mode: requestedMode });
          if (!active) return;
          const loadedForm = {
            ...initialForm,
            ...loaded.loan,
            DocDate: dateOnly(loaded.loan.DocDate),
            EffectivityDate: dateOnly(loaded.loan.EffectivityDate),
            MaturityDate: dateOnly(loaded.loan.MaturityDate),
            LoanAmount: money(loaded.loan.LoanAmount),
            InterestRate: rateText(loaded.loan.InterestRate),
            InterestCons: ({
              "EFFECTIVE INTEREST": "Effective Interest",
              "MONTHLY ADD-ON": "Monthly Add-On",
              COMPOUNDED: "Compounded",
              SIMPLE: "Simple",
            })[String(loaded.loan.InterestCons || "").trim().toUpperCase()] || "Monthly Add-On",
            InterestPaymentTerms: interestPaymentTermOptions.find(
              (option) => option.toUpperCase() === String(loaded.loan.InterestPaymentTerms || "").trim().toUpperCase(),
            ) || "",
            PrincipalPaymentTerms: principalPaymentTermOptions.find(
              (option) => option.toUpperCase() === String(loaded.loan.PrincipalPaymentTerms || "").trim().toUpperCase(),
            ) || "",
            TotalInterest: money(loaded.loan.TotalInterest),
            MaturityValue: money(loaded.loan.MaturityValue),
          };
          setSavedJournalRows(loaded.journalEntries || []);
          if (cloning) {
            loadedForm.DocNo = "";
            loadedForm.DocDate = today();
            loadedForm.Status = "New";
            if (defaultBranch) loadedForm.Branch = branchCode(defaultBranch);
          }
          setForm(loadedForm);
          if (loaded.borrower && !(options.borrowers || []).some((item) => borrowerCode(item) === borrowerCode(loaded.borrower))) {
            setBorrowers((current) => [...current, loaded.borrower]);
          }
        } else {
          setForm((current) => ({ ...current, Branch: branchCode(defaultBranch) }));
        }
      } catch (error) {
        if (active) setNotice({ severity: "error", text: error.message });
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // Load the form once for the route that opened it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compID, recID, requestedMode, routeBranch, userID]);

  const fieldSx = viewing ? {} : {
    "& .MuiOutlinedInput-root": { bgcolor: theme.palette.mode === "dark" ? "rgba(255,193,7,.10)" : "#fff8dc" },
  };
  const computedFieldSx = {
    "& .MuiOutlinedInput-root": { bgcolor: motif.panel },
    "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: motif.text, fontWeight: 700 },
  };
  const updateField = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const normalizeAmount = (field) => updateField(field, money(form[field]));
  const changeLoanType = (value) => setForm((current) => ({
    ...current,
    Type: value,
    RefNo: value === "NEW" ? "" : current.RefNo,
  }));
  const changeBorrower = (value) => setForm((current) => ({
    ...current,
    Borrower: borrowerCode(value),
    RefNo: "",
  }));
  const changeBranch = (value) => setForm((current) => {
    const borrower = borrowers.find((item) => borrowerCode(item) === current.Borrower);
    return {
      ...current,
      Branch: value,
      Borrower: borrower && String(borrower.Branch || "") !== String(value) ? "" : current.Borrower,
      RefNo: "",
    };
  });

  const validate = () => {
    if (!form.DocDate) return "Document date is required.";
    if (!form.Borrower) return "Select a borrower.";
    if (!form.Branch) return "Select the branch responsible for this loan.";
    if (!form.Type.trim()) return "Loan type is required.";
    if (referenceRequired && !form.RefNo) return `Select the active or posted loan being ${form.Type === "RENEW" ? "renewed" : "revised"}.`;
    if (!form.EffectivityDate) return "Effectivity date is required.";
    if (numberValue(form.LoanAmount) <= 0) return "Loan amount must be greater than zero.";
    if (numberValue(form.InterestRate) < 0) return "Interest rate cannot be negative.";
    if (!["Monthly Add-On", "Effective Interest", "Simple", "Compounded"].includes(form.InterestCons)) return "Select a valid interest condition.";
    if (!Number.isInteger(Number(form.LoanTerms)) || Number(form.LoanTerms) <= 0) return "Loan term must be a whole number greater than zero.";
    if (!form.LoanTermsCons.trim()) return "Select or enter the loan-term unit.";
    if (!computedSummary.MaturityDate) return "Complete the effectivity date and loan term to calculate the maturity date.";
    if (!form.Particulars.trim()) return "Particulars are required.";
    return "";
  };

  const save = async () => {
    const validation = validate();
    if (validation) return setNotice({ severity: "warning", text: validation });
    try {
      setSaving(true);
      const data = await post("/api/lending/loans/save", {
        compID, userID, mode: editing ? "edit" : "new", recID: editing ? recID : undefined,
        loan: { ...form, ...computedSummary },
      });
      setForm((current) => ({ ...current, DocNo: data.docNo, Status: data.status || current.Status }));
      setNotice({ severity: "success", text: data.message });
      window.setTimeout(() => navigate("/App/LoanList"), 900);
    } catch (error) {
      setNotice({ severity: "error", text: error.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1, md: 1.5 }, minHeight: "100%", bgcolor: motif.page }}>
      <Stack spacing={1.25}>
        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}`, borderTop: `4px solid ${motif.main}` }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }} gap={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ width: 44, height: 44, display: "grid", placeItems: "center", flexShrink: 0, borderRadius: 2.5, bgcolor: motif.main, color: theme.palette.primary.contrastText }}>
                <AccountBalanceWalletOutlined />
              </Box>
              <Box>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                  <Typography variant="h5" fontWeight={800} color={motif.text}>{viewing ? "View" : editing ? "Edit" : cloning ? "Clone" : "New"} Loan</Typography>
                  <Chip size="small" label={String(form.Status || "New").toUpperCase()} color={String(form.Status).toUpperCase() === "POSTED" ? "success" : "warning"} variant="outlined" />
                </Stack>
                <Typography color={motif.muted}>{form.DocNo || "Document number will be generated automatically when saved."}</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button startIcon={<ArrowBackOutlined />} color="inherit" onClick={() => navigate("/App/LoanList")}>Back</Button>
              {!viewing && (
                <Button variant="contained" startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveOutlined />} disabled={loading || saving} onClick={save} sx={{ minWidth: 120 }}>
                  {saving ? (editing ? "Updating…" : "Saving…") : (editing ? "Update" : "Save")}
                </Button>
              )}
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}`, overflow: "hidden" }}>
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" scrollButtons="auto" sx={{ px: 1 }}>
            <Tab label="Loan Details" />
            <Tab label="Journal Entry" />
          </Tabs>
        </Paper>

        <Box sx={{ display: activeTab === 0 ? "contents" : "none" }}>
        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
            <Box>
              <Typography variant="h6" fontWeight={800} color={motif.text}>Loan Information</Typography>
              <Typography variant="body2" color={motif.muted}>Identify the borrower and the branch responsible for the loan.</Typography>
            </Box>
            <Typography variant="caption" color={motif.muted}>Yellow fields are editable · * Required</Typography>
          </Stack>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))", lg: "1.1fr 2.6fr 1.4fr 1.4fr 1.4fr" }, gap: 1 }}>
            <TextField size="small" type="date" label="Document Date *" value={form.DocDate} onChange={(event) => updateField("DocDate", event.target.value)} disabled={viewing} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <Autocomplete
              options={availableBorrowers}
              value={selectedBorrower}
              onChange={(_, value) => changeBorrower(value)}
              getOptionLabel={(option) => `${borrowerCode(option)} - ${borrowerName(option)}`}
              isOptionEqualToValue={(option, value) => borrowerCode(option) === borrowerCode(value)}
              disabled={viewing || !form.Branch}
              renderInput={(inputParams) => <TextField {...inputParams} size="small" label="Borrower *" placeholder={form.Branch ? "Search by borrower code or name" : "Select a branch first"} sx={fieldSx} />}
            />
            <TextField size="small" select label="Loan Type *" value={form.Type} onChange={(event) => changeLoanType(event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={fieldSx}>
              <option value="NEW">NEW</option>
              <option value="RENEW">RENEW</option>
              <option value="REVISE">REVISE</option>
            </TextField>
            <Autocomplete
              options={availableReferenceLoans}
              value={selectedReferenceLoan}
              onChange={(_, value) => updateField("RefNo", value?.DocNo || "")}
              getOptionLabel={(option) => String(option?.DocNo || "")}
              isOptionEqualToValue={(option, value) => String(option.DocNo) === String(value.DocNo)}
              disabled={viewing || !referenceRequired || !form.Borrower || !form.Branch}
              noOptionsText="No active or posted loans found"
              renderOption={(optionProps, option) => (
                <Box component="li" {...optionProps} key={option.RecID} sx={{ display: "block !important" }}>
                  <Typography fontWeight={800}>{option.DocNo} · {option.Status}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dateOnly(option.DocDate)} · Loan amount: ₱{money(option.LoanAmount)}
                  </Typography>
                </Box>
              )}
              renderInput={(inputParams) => (
                <TextField
                  {...inputParams}
                  size="small"
                  label={`Reference No.${referenceRequired ? " *" : ""}`}
                  placeholder={referenceRequired ? "Search active or posted loans" : "Available for RENEW or REVISE"}
                  helperText={referenceRequired ? "Select the original loan transaction." : "Change Loan Type to RENEW or REVISE to enable."}
                  sx={fieldSx}
                />
              )}
            />
            <TextField size="small" select label="Branch *" value={form.Branch} onChange={(event) => changeBranch(event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={fieldSx}>
              <option value="" />
              {branches.map((branch) => <option key={branchCode(branch)} value={branchCode(branch)}>{branchCode(branch)} - {branchName(branch)}</option>)}
            </TextField>
          </Box>
          {selectedBorrower && (
            <Alert severity="info" variant="outlined" icon={<PersonOutline />} sx={{ mt: 1, py: 0, bgcolor: motif.panel }}>
              <b>{borrowerName(selectedBorrower)}</b>{selectedBorrower.Alias ? ` · ${selectedBorrower.Alias}` : ""}
              {selectedBorrower.TINAll ? ` · TIN: ${selectedBorrower.TINAll}` : ""}
              {selectedBorrower.Address ? ` · ${selectedBorrower.Address}` : ""}
            </Alert>
          )}
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
          <SectionHeader icon={<PaymentsOutlined />} title="Loan Setup" subtitle="Enter the principal, interest conditions, loan period, and applicable time basis." motif={motif} />
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
            <AmountField label="Loan Amount" field="LoanAmount" value={form.LoanAmount} onChange={updateField} onBlur={normalizeAmount} disabled={viewing} required sx={fieldSx} />
            <TextField size="small" label="Monthly Add-On Interest Rate (%) *" value={form.InterestRate} onChange={(event) => updateField("InterestRate", editableRate(event.target.value))} disabled={viewing} inputProps={{ inputMode: "decimal" }} helperText="Applied monthly to the original loan amount" sx={fieldSx} />
            <TextField size="small" select label="Interest Condition *" value={form.InterestCons} onChange={(event) => updateField("InterestCons", event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={{ ...fieldSx, gridColumn: { sm: "1 / -1", lg: "span 2" } }}>
              <option value="Monthly Add-On">Monthly Add-On</option>
              <option value="Effective Interest">Effective Interest</option>
              <option value="Simple">Simple</option>
              <option value="Compounded">Compounded</option>
            </TextField>
            <TextField size="small" label="Loan Term *" value={form.LoanTerms} onChange={(event) => updateField("LoanTerms", event.target.value.replace(/\D/g, "").slice(0, 9))} disabled={viewing} inputProps={{ inputMode: "numeric" }} sx={fieldSx} />
            <TextField size="small" select label="Loan Term Unit *" value={form.LoanTermsCons} onChange={(event) => updateField("LoanTermsCons", event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={fieldSx}>
              <option value="Days">Days</option>
              <option value="Weeks">Weeks</option>
              <option value="Months">Months</option>
              <option value="Years">Years</option>
            </TextField>
            <TextField size="small" type="date" label="Effectivity Date *" value={form.EffectivityDate} onChange={(event) => updateField("EffectivityDate", event.target.value)} disabled={viewing} InputLabelProps={{ shrink: true }} sx={fieldSx} />
            <TextField size="small" select label="Time Basis *" value={form.TimeBased} onChange={(event) => updateField("TimeBased", event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={fieldSx}>
              <option value="Regular(360days)">Regular (360 days)</option>
              <option value="Term(365days)">Term (365 days)</option>
            </TextField>
          </Box>
        </Paper>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(0, 1fr)" }, gap: 1.25 }}>
          <Paper elevation={0} sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
            <SectionHeader icon={<CalendarMonthOutlined />} title="Payment Schedule" subtitle="Record the agreed interest and principal payment terms." motif={motif} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
              <TextField size="small" select label="Interest Payment Term" value={form.InterestPaymentTerms} onChange={(event) => updateField("InterestPaymentTerms", event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={fieldSx}>
                <option value="">Select payment term</option>
                {interestPaymentTermOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </TextField>
              <TextField size="small" select label="Principal Payment Term" value={form.PrincipalPaymentTerms} onChange={(event) => updateField("PrincipalPaymentTerms", event.target.value)} disabled={viewing} SelectProps={{ native: true }} sx={fieldSx}>
                <option value="">Select payment term</option>
                {principalPaymentTermOptions.map((option) => <option key={option} value={option}>{option}</option>)}
              </TextField>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
            <SectionHeader icon={<ReceiptLongOutlined />} title="Loan Summary" subtitle="These values are calculated automatically from the loan setup." motif={motif} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1 }}>
              <AmountField label="Monthly Payment" field="MonthlyPayment" value={computedSummary.MonthlyPayment} onChange={updateField} onBlur={normalizeAmount} disabled sx={computedFieldSx} />
              <AmountField label="Total Interest" field="TotalInterest" value={computedSummary.TotalInterest} onChange={updateField} onBlur={normalizeAmount} disabled sx={computedFieldSx} />
              <AmountField label="Maturity Value" field="MaturityValue" value={computedSummary.MaturityValue} onChange={updateField} onBlur={normalizeAmount} disabled sx={computedFieldSx} />
              <TextField size="small" type="date" label="Maturity Date" value={computedSummary.MaturityDate} disabled InputLabelProps={{ shrink: true }} sx={computedFieldSx} />
            </Box>
            <Divider sx={{ my: 1, borderColor: motif.border }} />
            <Alert severity="info" variant="outlined" sx={{ py: 0, bgcolor: motif.panel }}>
              Total Interest, Maturity Value, and Maturity Date cannot be modified manually.
            </Alert>
          </Paper>
        </Box>

        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
          <SectionHeader icon={<ReceiptLongOutlined />} title="Loan Particulars" subtitle="Record the purpose or description of the loan." motif={motif} />
          <TextField size="small" label="Particulars *" value={form.Particulars} onChange={(event) => updateField("Particulars", event.target.value)} disabled={viewing} multiline minRows={1} maxRows={2} inputProps={{ maxLength: 50 }} helperText={`${form.Particulars.length}/50 characters`} fullWidth sx={fieldSx} />
        </Paper>
        </Box>

        <Paper elevation={0} sx={{ display: activeTab === 1 ? "block" : "none", p: { xs: 1.5, md: 1.75 }, borderRadius: 3, bgcolor: motif.paper, border: `1px solid ${motif.border}` }}>
          <SectionHeader icon={<ReceiptLongOutlined />} title="Generated Journal Entry" subtitle="This balanced entry is regenerated whenever the loan transaction is saved." motif={motif} />
          {!journalConfigured && <Alert severity="error" variant="outlined" sx={{ mb: 1.5 }}>Configure GLLENAR and GLLENAP in Systemadmin with valid Chart of Account values before saving this loan.</Alert>}
          <TableContainer sx={{ border: `1px solid ${motif.border}`, borderRadius: 2 }}>
            <Table size="small">
              <TableHead><TableRow sx={{ bgcolor: motif.panel }}><TableCell>Account</TableCell><TableCell>Account Title</TableCell><TableCell>Agent Code</TableCell><TableCell>Particulars</TableCell><TableCell align="right">Debit</TableCell><TableCell align="right">Credit</TableCell></TableRow></TableHead>
              <TableBody>
                {journalRows.map((row, index) => <TableRow key={`${row.Code || row.AccountCode}-${index}`}><TableCell><Typography fontWeight={800}>{row.AccountCode || "Not configured"}</Typography>{row.Code && <Typography variant="caption" color="text.secondary">{row.Code}</Typography>}</TableCell><TableCell>{row.AccountTitle || "—"}</TableCell><TableCell>{row.AgentCode || "—"}</TableCell><TableCell>{row.Particulars || form.Particulars || "—"}</TableCell><TableCell align="right">₱{money(row.Debit)}</TableCell><TableCell align="right">₱{money(row.Credit)}</TableCell></TableRow>)}
                <TableRow sx={{ "& td": { fontWeight: 900, borderTop: `2px solid ${motif.main}` } }}><TableCell colSpan={4}>TOTAL</TableCell><TableCell align="right">₱{money(journalRows.reduce((sum, row) => sum + row.Debit, 0))}</TableCell><TableCell align="right">₱{money(journalRows.reduce((sum, row) => sum + row.Credit, 0))}</TableCell></TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Alert severity={journalConfigured && form.Borrower && numberValue(form.LoanAmount) > 0 ? "success" : "info"} variant="outlined" sx={{ mt: 1.5 }}>
            {journalConfigured && form.Borrower && numberValue(form.LoanAmount) > 0 ? "The journal entry is balanced and ready to save." : "Complete the borrower, loan amount, and account configuration to generate the final journal entry."}
          </Alert>
        </Paper>
      </Stack>

      <Snackbar open={Boolean(notice)} autoHideDuration={notice?.severity === "success" ? 2400 : 6500} onClose={() => setNotice(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={notice?.severity || "info"} variant="filled" onClose={() => setNotice(null)} sx={{ minWidth: { xs: "90vw", sm: 460 }, boxShadow: 6 }}>{notice?.text}</Alert>
      </Snackbar>
      <Backdrop open={loading || saving} sx={{ zIndex: (muiTheme) => muiTheme.zIndex.modal + 1, color: "#fff", bgcolor: "rgba(0,0,0,.60)" }}>
        <Stack alignItems="center" spacing={2} textAlign="center">
          <CircularProgress color="inherit" />
          <Typography variant="h6" fontWeight={800}>{loading ? "Loading loan information…" : editing ? "Updating loan transaction…" : "Saving loan transaction…"}</Typography>
        </Stack>
      </Backdrop>
    </Box>
  );
};

export default Loans;
